import { NotDeliveredReason, ShortReason, type Route, type RouteStop, type StopDeliver } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, isNetworkError } from '../api';
import { dismissNotSent, queueDelivery } from '../deliveryQueue';
import { useDeliveryQueue } from '../components/DeliverySync';
import { useAuth } from '../auth';
import { IconPhone } from '../components/icons';
import { SignaturePad } from '../components/SignaturePad';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatPhone, formatQty, isIsoDate, parseTyped, todayIso } from '../format';
import { useSearchParam, useUnsaved } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/** A route the server sent back, put in its place in the day's list: the cards never jump (decision 182). */
const replaceRoute = (cur: Route[] | undefined, r: Route): Route[] => (cur?.some((x) => x.id === r.id) ? cur.map((x) => (x.id === r.id ? r : x)) : [...(cur ?? []), r]);

/** What the driver collects at the door: what is owed on the invoice, less what came off it at the door; with no
 * invoice yet, what went off the truck at the order's prices (decision 182). Null when it cannot be worked out. */
export function toCollect(s: RouteStop, qtyOf: (lineId: string) => number | null | undefined): number | null {
  const off = (l: RouteStop['lines'][number]) => { const q = qtyOf(l.id); return q === undefined || q === null ? l.qty : q; };
  // an invoice whose balance is not known here (before the ledger's start): the office says what to collect
  if (s.invoiceId !== null && s.invoiceBalance === null) return null;
  if (s.invoiceBalance !== null) {
    let short = 0;
    for (const l of s.lines) {
      const q = off(l);
      if (q < l.qty) { if (l.unitPrice === null) return null; short += (l.qty - q) * l.unitPrice; }
    }
    return Math.max(0, Math.round((s.invoiceBalance - short) * 100) / 100);
  }
  let sum = 0;
  for (const l of s.lines) { if (l.unitPrice === null) return null; sum += off(l) * l.unitPrice; }
  return Math.round(sum * 100) / 100;
}

/** The day's route: the office builds and orders it; the driver taps through the stops (docs/VISION.md row 11). */
export function RoutePage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const [dateRaw, setDate] = useSearchParam('date');
  const date = isIsoDate(dateRaw) ? dateRaw : todayIso();
  const routes = useQuery({ queryKey: qk.routes(date), queryFn: ({ signal }) => api.routes(date, signal) });
  const office = me?.role === 'owner' || me?.role === 'office';
  // who can be given a route: the office picks too, not only the owner (decision 182)
  const drivers = useQuery({ queryKey: qk.routeDrivers, queryFn: ({ signal }) => api.routeDrivers(signal), enabled: office });
  // a driver works the route given to them (or one given to no one), as the server allows
  const onRoad = (r: { driverId: string | null }) => office || (me?.role === 'driver' && (r.driverId === null || r.driverId === me.id)) || (me?.role === 'warehouse' && r.driverId === me.id);
  const put = (r: Route) => qc.setQueryData(qk.routes(date), (cur: Route[] | undefined) => replaceRoute(cur, r));
  const build = useMutation({ mutationFn: () => api.buildRoute(date), onSuccess: (rs) => qc.setQueryData(qk.routes(date), rs) });
  const patch = useMutation({ mutationFn: (v: { id: string; body: Parameters<typeof api.patchRoute>[1] }) => api.patchRoute(v.id, v.body), onSuccess: put });
  const start = useMutation({ mutationFn: (id: string) => api.startRoute(id), onSuccess: (r) => { put(r); void qc.invalidateQueries({ queryKey: ['orders'] }); } });
  const finish = useMutation({ mutationFn: (id: string) => api.finishRoute(id), onSuccess: (r) => { put(r); void qc.invalidateQueries({ queryKey: ['orders'] }); } });
  // deliveries kept on the phone with no signal (decision 109): sent from any screen by DeliverySync; here, Send now
  const { queued, notSent, sending, flush } = useDeliveryQueue();
  const [newName, setNewName] = useState('');
  const add = useMutation({ mutationFn: (name: string) => api.createRoute(date, name), onSuccess: (r) => { setNewName(''); qc.setQueryData(qk.routes(date), (cur: Route[] | undefined) => replaceRoute(cur, r)); } });

  return (
    <div className="stack">
      <PageHead title={t('routesTitle')} back="/" />
      <p className="muted">{t('routesHint')}</p>
      <div className="day-nav">
        <button type="button" className="btn btn-ghost" onClick={() => setDate(addDays(date, -1))} aria-label={t('prevDay')}>
          ‹
        </button>
        <strong>{formatDate(date, intlLocale, 'long')}</strong>
        <button type="button" className="btn btn-ghost" onClick={() => setDate(addDays(date, 1))} aria-label={t('nextDay')}>
          ›
        </button>
      </div>
      {office ? (
        <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => build.mutate()} disabled={build.isPending}>
          {t('buildRoute')}
        </button>
      ) : null}
      {office ? (
        <form
          className="picker-head"
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) add.mutate(newName.trim());
          }}
        >
          <label className="field" style={{ flex: 1 }}>
            <span>{t('newRouteName')}</span>
            <input type="text" value={newName} maxLength={40} autoComplete="off" placeholder={t('newRoutePlaceholder')} onChange={(e) => setNewName(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-secondary" disabled={add.isPending || !newName.trim()}>
            + {t('addRoute')}
          </button>
        </form>
      ) : null}
      {queued.length ? (
        <div className="flash" role="status" data-testid="delivery-queue">
          {t('deliveriesOnPhone').replace('{n}', String(queued.length))}{' '}
          <button type="button" className="btn btn-secondary" disabled={sending} onClick={() => void flush()}>
            {t('sendNow')}
          </button>
        </div>
      ) : null}
      {notSent.map((r) => (
        <div key={`${r.delivery.stopId}-${r.delivery.savedAt}`} className="field-error" role="alert" data-testid="not-sent">
          {r.delivery.customerName}: {t('deliveryNotSent')} {r.why}
          {r.delivery.body.collected ? ` · ${t('collected')} ${formatMoney(r.delivery.body.collected.amount, intlLocale)}` : ''}{' '}
          <button type="button" className="btn btn-ghost" onClick={() => dismissNotSent(r.delivery.stopId, r.delivery.savedAt)}>
            {t('dismiss')}
          </button>
        </div>
      ))}
      {add.error ? <ErrorBox error={add.error} /> : null}
      {build.error ? <ErrorBox error={build.error} /> : null}
      {patch.error ? <ErrorBox error={patch.error} /> : null}
      {start.error ? <ErrorBox error={start.error} /> : null}
      {finish.error ? <ErrorBox error={finish.error} /> : null}
      <QueryView query={routes}>
        {(rs) =>
          rs.length === 0 ? (
            <Empty>{t('noRoute')}</Empty>
          ) : (
            rs.map((r) => (
              <section key={r.id} className="card stack-sm" data-testid="route">
                <h2>
                  {r.name} · {r.stops.length} {t('stops')}
                  {r.status === 'out' ? <span className="badge badge-warn">{t('routeOut')}</span> : null}
                  {r.status === 'done' ? <span className="badge badge-ok">{t('routeDone')}</span> : null}
                </h2>
                {office ? (
                  <div className="picker-head">
                    <label className="field" style={{ flex: 1 }}>
                      <span>{t('driver')}</span>
                      {drivers.data ? (
                        <select value={r.driverId ?? ''} aria-label={t('driver')} onChange={(e) => patch.mutate({ id: r.id, body: { driverId: e.target.value || null } })}>
                          <option value="">—</option>
                          {r.driverId && !drivers.data.some((u) => u.id === r.driverId) ? <option value={r.driverId}>{r.driverName ?? '—'}</option> : null}
                          {drivers.data.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" value={r.driverName ?? ''} readOnly aria-label={t('driver')} />
                      )}
                    </label>
                    <label className="field">
                      <span>{t('truck')}</span>
                      <input type="text" defaultValue={r.truck ?? ''} aria-label={t('truck')} onBlur={(e) => { if ((e.target.value || null) !== r.truck) patch.mutate({ id: r.id, body: { truck: e.target.value || null } }); }} />
                    </label>
                  </div>
                ) : (
                  <p className="muted">
                    {t('driver')}: {r.driverName ?? '—'}
                    {r.truck ? ` · ${r.truck}` : ''}
                  </p>
                )}
                {r.stops.length > 0 ? (
                  <div className="contact-actions">
                    <Link className="btn btn-ghost" to={`/route/${r.id}/print?date=${date}&what=load`}>
                      {t('loadSheet')}
                    </Link>
                    {office ? (
                      <Link className="btn btn-ghost" to={`/route/${r.id}/print?date=${date}&what=invoices`}>
                        {t('printInvoices')}
                      </Link>
                    ) : null}
                  </div>
                ) : null}
                {r.cash ? <RouteCashBox route={r} office={office} date={date} /> : null}
                {onRoad(r) && r.status === 'planned' && r.stops.length > 0 ? (
                  <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => start.mutate(r.id)} disabled={start.isPending}>
                    {t('startRoute')}
                  </button>
                ) : null}
                {onRoad(r) && r.status === 'out' && (office || r.driverId === me?.id) ? (
                  (() => {
                    // a delivery still on the phone would be closed as not reached: it is sent first
                    const waiting = r.stops.some((s) => queued.some((q) => q.stopId === s.id));
                    return (
                      <>
                        <button type="button" className="btn btn-secondary btn-block" data-testid="finish-route" onClick={() => { if (window.confirm(t('finishRouteConfirm'))) finish.mutate(r.id); }} disabled={finish.isPending || waiting}>
                          {t('finishRoute')}
                        </button>
                        {waiting ? <p className="muted">{t('finishWaitPhone')}</p> : null}
                      </>
                    );
                  })()
                ) : null}
                <ol className="list" data-testid="stops">
                  {r.stops.map((s, i) => (
                    <StopRow
                      key={s.id}
                      stop={s}
                      route={r}
                      date={date}
                      onRoad={onRoad(r)}
                      queued={queued.some((q) => q.stopId === s.id)}
                      others={office && r.status === 'planned' ? rs.filter((x) => x.id !== r.id && x.status === 'planned' && x.siteId === r.siteId) : []}
                      move={
                        office && r.status === 'planned'
                          ? (dir) => {
                              const ids = r.stops.map((x) => x.id);
                              const j = i + dir;
                              if (j < 0 || j >= ids.length) return;
                              [ids[i], ids[j]] = [ids[j]!, ids[i]!];
                              patch.mutate({ id: r.id, body: { stopOrder: ids } });
                            }
                          : null
                      }
                    />
                  ))}
                </ol>
              </section>
            ))
          )
        }
      </QueryView>
    </div>
  );
}

/** What the truck collected, by method, and the office's sign-off on the cash counted. */
function RouteCashBox({ route: r, office, date }: { route: Route; office: boolean; date: string }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const c = r.cash!;
  // blank until counted: a box that starts at what was collected invites a tap without counting (end-user test)
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const turnIn = useMutation({
    mutationFn: () => api.cashTurnIn(r.id, { amount: Math.round(Number(amount) * 100) / 100, ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: (route) => qc.setQueryData(qk.routes(date), (cur: Route[] | undefined) => replaceRoute(cur, route)),
  });
  return (
    <section className="card stack-sm" data-testid="route-cash">
      <h3>{t('cashOnRoute')}</h3>
      <dl className="facts plain">
        <div className="fact">
          <dt>{t('collected')}</dt>
          <dd className="money">{formatMoney(c.collected, intlLocale)}</dd>
        </div>
        <div className="fact">
          <dt>{t('method_cash')}</dt>
          <dd className="money">{formatMoney(c.cash, intlLocale)}</dd>
        </div>
        <div className="fact">
          <dt>{t('checks')}</dt>
          <dd className="money">{formatMoney(c.checks, intlLocale)}</dd>
        </div>
        {c.other > 0 ? (
          <div className="fact">
            <dt>{t('method_other')}</dt>
            <dd className="money">{formatMoney(c.other, intlLocale)}</dd>
          </div>
        ) : null}
      </dl>
      {c.turnedInAt ? (
        <p className="note">
          {t('turnedIn')}: {formatMoney(c.turnedInAmount ?? 0, intlLocale)} · {c.turnedInBy ?? '—'}
          {c.turnedInNote ? ` · ${c.turnedInNote}` : ''}
          {/* counted against the cash the truck collected: short or over said in dollars */}
          {c.turnedInAmount !== null && Math.abs(c.turnedInAmount - c.cash) >= 0.005 ? (
            <strong className="bad-text" data-testid="cash-variance">
              {' · '}
              {(c.turnedInAmount < c.cash ? t('cashShortBy') : t('cashOverBy')).replace('{amount}', formatMoney(Math.abs(c.turnedInAmount - c.cash), intlLocale))}
            </strong>
          ) : null}
        </p>
      ) : office ? (
        <div className="contact-actions">
          <label className="field" style={{ flex: 1 }}>
            <span>{t('countedAmount')}</span>
            <input className="count-input" type="text" inputMode="decimal" autoComplete="off" aria-label={t('countedAmount')} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span>{t('notes')}</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" aria-label={t('notes')} />
          </label>
          <button type="button" className="btn btn-primary" disabled={turnIn.isPending || amount.trim() === '' || !(Number(amount) >= 0)} onClick={() => turnIn.mutate()}>
            {t('turnInCash')}
          </button>
        </div>
      ) : (
        <p className="muted">{t('notTurnedIn')}</p>
      )}
      {turnIn.isError ? <ErrorBox error={turnIn.error} /> : null}
    </section>
  );
}

function StopRow({ stop: s, route, date, onRoad, queued, others, move }: { stop: RouteStop; route: Route; date: string; onRoad: boolean; queued: boolean; others: Route[]; move: ((dir: -1 | 1) => void) | null }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const put = (r: Route) => {
    qc.setQueryData(qk.routes(date), (cur: Route[] | undefined) => replaceRoute(cur, r));
    void qc.invalidateQueries({ queryKey: ['orders'] });
  };
  const [open, setOpen] = useState(false);
  const [receivedBy, setReceivedBy] = useState('');
  const [note, setNote] = useState('');
  const [qty, setQty] = useState<Record<string, string>>({});
  const [minutes, setMinutes] = useState('15');
  const [collected, setCollected] = useState('');
  const [method, setMethod] = useState<'cash' | 'check' | 'card' | 'zelle' | 'other'>('cash');
  const [reference, setReference] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [why, setWhy] = useState<Record<string, ShortReason>>({});
  const moveTo = useMutation({ mutationFn: (routeId: string) => api.moveStop(s.id, routeId), onSuccess: (rs) => qc.setQueryData(qk.routes(date), rs) });
  const arrive = useMutation({ mutationFn: () => api.arriveStop(s.id), onSuccess: put });
  // cash kept at the door while QuickBooks holds the payments: the office undoes a wrong amount (decision 126)
  const undoCash = useMutation({ mutationFn: () => api.stopCashReverse(s.id), onSuccess: put });
  // what the driver typed, read the same way everywhere ("$120", "1,200", "2,5"); typed but unreadable blocks Deliver
  const cash = parseTyped(collected);
  const cashBad = collected.trim() !== '' && (cash === null || cash < 0);
  const qtyOf = (id: string) => (qty[id] !== undefined && qty[id]!.trim() !== '' ? parseTyped(qty[id]!) : undefined);
  const qtyBad = (id: string) => qtyOf(id) === null || (qtyOf(id) ?? 0) < 0;
  const blocked = cashBad || s.lines.some((l) => qtyBad(l.id));
  const deliveryBody = (): StopDeliver => ({
    ...(receivedBy.trim() ? { receivedBy: receivedBy.trim() } : {}),
    ...(signature ? { signaturePng: signature } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
    ...(cash !== null && cash > 0 ? { collected: { amount: Math.round(cash * 100) / 100, method, ...(reference.trim() ? { reference: reference.trim() } : {}) } } : {}),
    lines: s.lines.map((l) => {
      const q = qtyOf(l.id) ?? l.qty;
      return { lineId: l.id, qtyDelivered: q, ...(q < l.qty && why[l.id] ? { shortReason: why[l.id] } : {}) };
    }),
  });
  const [noRoom, setNoRoom] = useState(false);
  const deliver = useMutation({
    mutationFn: (body: StopDeliver) => api.deliverStop(s.id, body),
    // tried even when the phone says it has no signal, so the failure lands here and the delivery is kept
    networkMode: 'always',
    onSuccess: (r) => {
      setOpen(false);
      put(r);
    },
    // no signal: kept on the phone and sent when it is back (decision 109)
    onError: (err, body) => {
      if (!isNetworkError(err)) return;
      const kept = queueDelivery({ stopId: s.id, date, customerName: s.customerName, body, savedAt: new Date().toISOString(), ...(me ? { userId: me.id } : {}) });
      setNoRoom(!kept);
      if (kept) setOpen(false);
    },
  });
  const eta = useMutation({ mutationFn: () => api.etaText(s.id, Number(minutes) || 15), onSuccess: put });
  // could not deliver (decision 182): why, and the order goes back for another day
  const [ndOpen, setNdOpen] = useState(false);
  const [ndReason, setNdReason] = useState<NotDeliveredReason | ''>('');
  const [ndNote, setNdNote] = useState('');
  // the office gives a stop not delivered its next day (an invoiced order too): it leaves this route
  const office = me?.role === 'owner' || me?.role === 'office';
  // the day after the route, and never a day gone by
  const [nextDay, setNextDay] = useState(() => { const d = addDays(date, 1); const today = todayIso(); return d < today ? today : d; });
  const anotherDay = useMutation({
    mutationFn: () => api.stopAnotherDay(s.id, { date: nextDay }),
    onSuccess: (rs) => { qc.setQueryData(qk.routes(date), rs); void qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  const notDeliver = useMutation({
    mutationFn: () => api.notDeliverStop(s.id, { reason: ndReason as NotDeliveredReason, ...(ndNote.trim() ? { note: ndNote.trim() } : {}) }),
    onSuccess: (r) => { setNdOpen(false); put(r); },
  });
  const photo = useMutation({ mutationFn: (f: File) => api.stopPhoto(s.id, f), onSuccess: () => void qc.invalidateQueries({ queryKey: qk.routes(date) }) });
  // a delivery being filled in at the door is work not saved: a new version of the app waits for it (decision 183)
  useUnsaved((open || ndOpen) && s.deliveredAt === null, t('deliveryUnsaved'));
  const done = s.deliveredAt !== null;
  const cancelled = s.orderStatus === 'cancelled';
  const notDone = s.notDeliveredAt !== null;
  const owed = s.cod || (s.invoiceBalance ?? 0) > 0 ? toCollect(s, qtyOf) : null;
  const shorts = s.lines.filter((l) => l.qtyDelivered !== null && l.qtyDelivered < l.qty);
  return (
    <li className={done || cancelled ? 'count-row stack-sm muted' : 'count-row stack-sm'} data-testid={`stop-${s.seq}`}>
      <div className="row count-head">
        <span className="qty-pill">{s.seq}</span>
        <span className="row-main">
          <span className="row-title">
            <Link to={`/orders/${s.orderId}`}>{s.customerName}</Link>
            {done ? <span className="badge badge-ok">{t('delivered')}</span> : cancelled ? <span className="badge badge-danger">{s.notDeliveredReason === 'cancelled' ? t('nd_cancelled') : t('status_cancelled')}</span> : notDone ? <span className="badge badge-danger">{t('notDelivered')}: {t(`nd_${s.notDeliveredReason ?? 'other'}`)}</span> : queued ? <span className="badge badge-warn">{t('savedOnPhone')}</span> : s.arrivedAt ? <span className="badge badge-warn">{t('arrived')}</span> : null}
            {s.callAhead ? <span className="badge badge-warn">{t('callAhead')}</span> : null}
          </span>
          <span className="row-sub">{s.address ?? t('noAddress')}{s.window ? ` · ${t('window')} ${s.window}` : ''}</span>
          {s.deliveryNotes ? <span className="row-sub">{s.deliveryNotes}</span> : null}
          {s.cod || (s.balance !== null && s.balance > 0) ? (
            <span className="row-sub" data-testid={`owes-${s.seq}`}>
              {s.cod ? <span className="badge badge-warn">{t('codBadge')}</span> : null}
              {s.balance !== null && s.balance > 0 ? (
                <>
                  {' '}
                  {t('owesNow')} <strong>{formatMoney(s.balance, intlLocale)}</strong>
                  {s.pastDue > 0 ? <span className="owed"> · {formatMoney(s.pastDue, intlLocale)} {t('pastDueShort')}</span> : null}
                </>
              ) : null}
            </span>
          ) : null}
          <span className="row-sub">
            {s.lines.map((l) => `${formatQty(l.qtyDelivered ?? l.qty)}${l.qtyDelivered !== null && l.qtyDelivered < l.qty ? `/${formatQty(l.qty)}` : ''} ${l.itemName}`).join(', ')} · {formatMoney(s.total, intlLocale)}
          </span>
          {notDone && !done && s.notDeliveredNote ? <span className="row-sub">{s.notDeliveredNote}</span> : null}
          {office && notDone && !done && !cancelled ? (
            <span className="contact-actions no-print" data-testid={`another-day-${s.seq}`}>
              <label className="field">
                <span>{t('anotherDay')}</span>
                <input type="date" value={nextDay} min={todayIso()} onChange={(e) => setNextDay(e.target.value)} aria-label={`${t('anotherDay')} ${s.seq}`} />
              </label>
              <button type="button" className="btn btn-secondary" disabled={anotherDay.isPending || !isIsoDate(nextDay) || nextDay === date} onClick={() => anotherDay.mutate()}>
                {t('anotherDayBtn')}
              </button>
              {anotherDay.error ? <ErrorBox error={anotherDay.error} /> : null}
            </span>
          ) : null}
          {done ? (
            <span className="row-sub">
              {t('receivedBy')}: {s.receivedBy ?? '—'}
              {shorts.length ? ` · ${shorts.length} ${t('short')}${shorts.some((l) => l.shortReason) ? ` (${[...new Set(shorts.flatMap((l) => (l.shortReason ? [t(`short_${l.shortReason}`)] : [])))].join(', ')})` : ''}` : ''}
              {s.note ? ` · ${s.note}` : ''}
              {s.hasSignature ? <> · <a href={api.stopSignatureUrl(s.id)} target="_blank" rel="noreferrer">{t('signed')}</a></> : null}
              {s.hasPhoto ? ` · ${t('photoDone')}` : ''}
              {s.collected ? ` · ${t('collected')} ${formatMoney(s.collected.amount, intlLocale)} (${t(`method_${s.collected.method}` as 'method_cash')}${s.collected.reference ? ` ${s.collected.reference}` : ''})` : ''}
              {s.collected?.kind === 'door' && (me?.role === 'owner' || me?.role === 'office') ? (
                <>
                  {' '}
                  <button type="button" className="btn-link no-print" disabled={undoCash.isPending} onClick={() => { if (window.confirm(t('stopCashUndoConfirm'))) undoCash.mutate(); }}>
                    {t('stopCashUndo')}
                  </button>
                </>
              ) : null}
              {undoCash.error ? <ErrorBox error={undoCash.error} /> : null}
            </span>
          ) : null}
        </span>
        {others.length > 0 && !done && !cancelled ? (
          <select className="no-print" aria-label={`${t('moveToRoute')} ${s.seq}`} value="" disabled={moveTo.isPending} onChange={(e) => { if (e.target.value) moveTo.mutate(e.target.value); }}>
            <option value="">{t('moveToRoute')}…</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : null}
        {move ? (
          <span className="contact-actions">
            <button type="button" className="btn btn-ghost" onClick={() => move(-1)} aria-label={`${t('moveUp')} ${s.seq}`}>
              ↑
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => move(1)} aria-label={`${t('moveDown')} ${s.seq}`}>
              ↓
            </button>
          </span>
        ) : null}
      </div>
      {/* the driver delivers once at the door, even when Leave with the truck was not tapped (decision 182) */}
      {/* a stop marked not delivered can still be delivered that day, even once the route closed (a second try) */}
      {onRoad && !done && !queued && !cancelled && (route.status !== 'done' || notDone) ? (
        <div className="quick-grid">
          {s.address ? (
            <a className="btn btn-secondary" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.address)}`} target="_blank" rel="noreferrer">
              {t('navigate')}
            </a>
          ) : null}
          {s.phone ? (
            <a className="btn btn-secondary" href={`tel:${s.phone}`}>
              <IconPhone /> {formatPhone(s.phone)}
            </a>
          ) : null}
          {!s.arrivedAt ? (
            <button type="button" className="btn btn-secondary" onClick={() => arrive.mutate()} disabled={arrive.isPending}>
              {t('arrived')}
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => { setOpen((v) => !v); setNdOpen(false); }}>
            {t('deliverBtn')}
          </button>
          {notDone ? null : (
            <button type="button" className="btn btn-ghost" onClick={() => { setNdOpen((v) => !v); setOpen(false); }}>
              {t('notDelivered')}
            </button>
          )}
          {s.etaTextedAt ? null : (
            <span className="contact-actions">
              <input className="count-input" type="text" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} aria-label={t('etaMinutes')} style={{ width: 64 }} />
              <button type="button" className="btn btn-ghost" onClick={() => eta.mutate()} disabled={eta.isPending}>
                {t('etaText')}
              </button>
            </span>
          )}
        </div>
      ) : null}
      {eta.error ? <ErrorBox error={eta.error} /> : null}
      {ndOpen && !done && !notDone ? (
        <form
          className="stack-sm"
          data-testid={`not-delivered-${s.seq}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (ndReason) notDeliver.mutate();
          }}
        >
          <label className="field">
            <span>{t('notDeliveredWhy')}</span>
            <select aria-label={t('notDeliveredWhy')} value={ndReason} onChange={(e) => setNdReason(NotDeliveredReason.parse(e.target.value))}>
              <option value="" disabled>
                {t('notDeliveredWhy')}…
              </option>
              {NotDeliveredReason.options.map((r) => (
                <option key={r} value={r}>
                  {t(`nd_${r}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('notes')}</span>
            <input type="text" value={ndNote} maxLength={500} onChange={(e) => setNdNote(e.target.value)} autoComplete="off" aria-label={`${t('notDelivered')} ${t('notes')}`} />
          </label>
          <p className="muted">{t('notDeliveredHint')}</p>
          {notDeliver.error ? <ErrorBox error={notDeliver.error} /> : null}
          <button type="submit" className="btn btn-secondary btn-lg btn-block" disabled={notDeliver.isPending || !ndReason}>
            {t('notDeliveredSave')}
          </button>
        </form>
      ) : null}
      {moveTo.error ? <ErrorBox error={moveTo.error} /> : null}
      {open && !done ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (!blocked) deliver.mutate(deliveryBody());
          }}
        >
          {s.lines.map((l) => (
            <label key={l.id} className="count-field">
              <span className="label">
                {t('qtyDelivered')} · {l.itemName} ({formatQty(l.qty)} {l.unit})
              </span>
              <input className="count-input" type="text" inputMode="decimal" autoComplete="off" aria-label={`${t('qtyDelivered')} ${l.itemName}`} value={qty[l.id] ?? ''} aria-invalid={qtyBad(l.id) || undefined} placeholder={formatQty(l.qty)} onChange={(e) => setQty((cur) => ({ ...cur, [l.id]: e.target.value }))} />
              {(qtyOf(l.id) ?? l.qty) < l.qty ? (
                <select aria-label={`${t('whyShort')} ${l.itemName}`} value={why[l.id] ?? ''} onChange={(e) => setWhy((cur) => ({ ...cur, [l.id]: ShortReason.parse(e.target.value) }))}>
                  <option value="" disabled>
                    {t('whyShort')}…
                  </option>
                  {ShortReason.options.map((r) => (
                    <option key={r} value={r}>
                      {t(`short_${r}`)}
                    </option>
                  ))}
                </select>
              ) : null}
            </label>
          ))}
          <label className="field">
            <span>{t('receivedBy')}</span>
            <input type="text" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} autoComplete="off" />
          </label>
          <SignaturePad label={t('signature')} onChange={setSignature} />
          <label className="field">
            <span>{t('doorNote')}</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
          </label>
          {/* once the office counted the route's cash, money taken now is entered at the office */}
          {route.cash?.turnedInAt ? null : (
          <div className="contact-actions">
            <label className="field" style={{ flex: 1 }}>
              <span>{t('collected')}</span>
              <input className="count-input" type="text" inputMode="decimal" autoComplete="off" placeholder="$" aria-label={t('collected')} aria-invalid={cashBad || undefined} value={collected} onChange={(e) => setCollected(e.target.value)} />
              {cashBad ? <small className="bad">{t('typedNotNumber')}</small> : null}
              {/* one tap fills what is owed, less what came off at the door (decision 182) */}
              {owed !== null && owed > 0 ? (
                <button type="button" className="btn btn-secondary" data-testid={`collect-fill-${s.seq}`} onClick={() => setCollected(owed.toFixed(2))}>
                  {t('collectFill').replace('{amount}', formatMoney(owed, intlLocale))}
                </button>
              ) : null}
            </label>
            <label className="field">
              <span>{t('method')}</span>
              <select aria-label={t('method')} value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
                {(['cash', 'check', 'card', 'zelle', 'other'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`method_${m}`)}
                  </option>
                ))}
              </select>
            </label>
            {method === 'check' ? (
              <label className="field">
                <span>{t('checkNo')}</span>
                <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} autoComplete="off" aria-label={t('checkNo')} style={{ width: 90 }} />
              </label>
            ) : null}
          </div>
          )}
          <label className="btn btn-secondary btn-block" style={{ cursor: 'pointer' }}>
            {photo.isSuccess ? `✓ ${t('photoDone')}` : t('photoPod')}
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} aria-label={t('photoPod')} onChange={(e) => { const f = e.target.files?.[0]; if (f) photo.mutate(f); e.target.value = ''; }} />
          </label>
          {deliver.error && !noRoom && !isNetworkError(deliver.error) ? <ErrorBox error={deliver.error} /> : null}
          {noRoom ? (
            <p className="field-error" role="alert">
              {t('deliveryNoRoom')}
            </p>
          ) : null}
          {photo.error ? <ErrorBox error={photo.error} /> : null}
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={deliver.isPending || blocked}>
            ✓ {t('deliverBtn')}
          </button>
        </form>
      ) : null}
    </li>
  );
}
